import type { CustomerFeeLineV2, FinancialSectionV2 } from "@credit-acceptance/step6-bff-client";

import { roundToDecimals, sumAmounts } from "../../../../lib/money";

/**
 * A customer fee line plus the per-row flags the BFF is expected to supply
 * (not in client 1.0.849 yet). Both are read defensively: a missing
 * `appliedToDlr` still renders the row, a missing `custFeeFlag` keeps the
 * Actual Amount read-only.
 */
export interface CustomerFeeRowData extends CustomerFeeLineV2 {
  /** The fee type is visible to the dealer; only these rows render. */
  appliedToDlr?: boolean;
  /** The Actual Amount may be adjusted (false for system-calculated fee types). */
  custFeeFlag?: boolean;
}

/** The financial section plus the state time-counting method the BFF is expected to supply. */
export type FinancialWithFeeContext = FinancialSectionV2 & { timeCountingMethod?: number };

/** One fee row as edited in the popup. */
export interface FeeEntry {
  key: string;
  feeName: string;
  riscAmount: number;
  /** The persisted actual amount the popup opened with. */
  originalActual: number;
  /** Raw text of the Actual Amount input. */
  input: string;
  /** True once the user has typed into this row's input. */
  edited: boolean;
}

export type FeeViolation =
  | { kind: "empty"; key: string; feeName: string }
  | { kind: "exceeds"; key: string; feeName: string; riscAmount: number }
  | { kind: "registrationExclusive" }
  | { kind: "smogExclusive" };

const EXCLUSIVE_FEE_PAIRS = {
  // State -> the pair of fee names that may not both carry a value, and the violation to raise.
  MA: { names: ["REGISTRATION", "REGISTRATION TRANSFER FEE"], kind: "registrationExclusive" },
  CA: { names: ["SMOG CERTIFICATION FEE", "SMOG EXEMPTION FEE"], kind: "smogExclusive" },
} as const;

/**
 * Keeps only what a money input may contain: digits, commas and a single period.
 * @param raw - The raw input text.
 * @returns {string} The sanitized text.
 */
export function sanitizeAmountInput(raw: string): string {
  const cleaned = raw.replaceAll(/[^\d,.]/g, "");
  const firstPeriod = cleaned.indexOf(".");
  if (firstPeriod === -1) {
    return cleaned;
  }
  return cleaned.slice(0, firstPeriod + 1) + cleaned.slice(firstPeriod + 1).replaceAll(".", "");
}

/**
 * Parses an Actual Amount input for math (commas stripped).
 * @param input - The raw input text.
 * @returns {number | undefined} The amount, or undefined when the input is empty or not a number.
 */
export function parseAmountInput(input: string): number | undefined {
  const stripped = input.replaceAll(",", "").trim();
  if (stripped === "" || stripped === ".") {
    return undefined;
  }
  const value = Number(stripped);
  return Number.isFinite(value) ? roundToDecimals(value) : undefined;
}

/**
 * Correction Amount = RISC Amount - Actual Amount.
 * @param riscAmount - The at-contract fee amount.
 * @param actualAmount - The adjusted amount.
 * @returns {number} The rounded correction.
 */
export function calculateCorrection(riscAmount: number, actualAmount: number): number {
  return roundToDecimals(riscAmount - actualAmount);
}

/**
 * The amount a row currently contributes to the totals: the typed value while it
 * is valid, otherwise the persisted one, so a half-typed or rejected entry never
 * skews the totals.
 * @param entry - The fee row.
 * @returns {number} The effective actual amount.
 */
export function effectiveActual(entry: FeeEntry): number {
  const parsed = parseAmountInput(entry.input);
  return parsed !== undefined && parsed <= entry.riscAmount ? parsed : entry.originalActual;
}

export interface FeeTotals {
  totalFee: number;
  totalActual: number;
  totalAdjusted: number;
}

/**
 * Rolls the rows up into the popup footer totals.
 * @param entries - The fee rows.
 * @returns {FeeTotals} Total Customer Fee, Total Actual Amt and the total adjustment.
 */
export function calculateFeeTotals(entries: FeeEntry[]): FeeTotals {
  const totalFee = sumAmounts(entries.map((entry) => ({ amount: entry.riscAmount })));
  const totalActual = sumAmounts(entries.map((entry) => ({ amount: effectiveActual(entry) })));
  return { totalFee, totalActual, totalAdjusted: roundToDecimals(totalFee - totalActual) };
}

/**
 * Total Adj Amt (F11.3). For states whose time-counting method is 2 the adjustment
 * is amortised over the term at the contract APR; otherwise it is the plain total.
 * Falls back to the plain total whenever the formula can't be evaluated.
 * @param totalAdjusted - The summed correction.
 * @param apr - Contract APR as a percentage (e.g. 19.99).
 * @param term - Contract term in months.
 * @param timeCountingMethod - The state's time-counting method.
 * @returns {number} The rounded Total Adj Amt.
 */
export function calculateTotalAdjAmt(
  totalAdjusted: number,
  apr?: number,
  term?: number,
  timeCountingMethod?: number
): number {
  if (timeCountingMethod !== 2 || !apr || !term || apr <= 0 || term <= 0) {
    return roundToDecimals(totalAdjusted);
  }
  const monthlyRate = apr / 100 / 12;
  const factor = (1 + monthlyRate) ** term;
  if (factor === 1) {
    return roundToDecimals(totalAdjusted);
  }
  return roundToDecimals(((totalAdjusted * monthlyRate * factor) / (factor - 1)) * term);
}

const normalizeName = (name: string) => name.trim().toUpperCase();

/**
 * Validates the edited rows (F11.1): a value is required, may not exceed the RISC
 * amount, and in MA / CA only one of each exclusive fee pair may carry a value.
 * Only rows the user edited are checked per-field; the exclusivity check runs on
 * the current values of both fees of the pair.
 * @param entries - The fee rows.
 * @param dealerState - The dealer state code, e.g. `"MA"`.
 * @returns {FeeViolation[]} Every violation found (empty when the entries are valid).
 */
export function validateFeeEntries(entries: FeeEntry[], dealerState?: string): FeeViolation[] {
  const violations: FeeViolation[] = [];

  for (const entry of entries) {
    if (!entry.edited) {
      continue;
    }
    const parsed = parseAmountInput(entry.input);
    if (parsed === undefined) {
      violations.push({ kind: "empty", key: entry.key, feeName: entry.feeName });
    } else if (parsed > entry.riscAmount) {
      violations.push({ kind: "exceeds", key: entry.key, feeName: entry.feeName, riscAmount: entry.riscAmount });
    }
  }

  const rule = EXCLUSIVE_FEE_PAIRS[normalizeName(dealerState ?? "") as keyof typeof EXCLUSIVE_FEE_PAIRS];
  if (rule) {
    const [first, second] = rule.names.map((name) => entries.find((entry) => normalizeName(entry.feeName) === name));
    const bothCarryValue =
      first &&
      second &&
      (first.edited || second.edited) &&
      (parseAmountInput(first.input) ?? 0) > 0 &&
      (parseAmountInput(second.input) ?? 0) > 0;
    if (bothCarryValue) {
      violations.push({ kind: rule.kind });
    }
  }

  return violations;
}
